import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Save,
  Shield,
  BadgeCheck,
  Loader2,
  UserCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api';
import { API_ROUTES, type Role } from '@avgrp/shared';
import { useAuth } from '@/hooks/useAuth';

// ─── Types ──────────────────────────────────────────────────

interface QualificationEntry {
  qualification: { name: string; code: string };
  [key: string]: unknown;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  role: Role;
  status: string;
  qualifications: (QualificationEntry | string)[];
  lastLogin: string | null;
  createdAt: string;
}

interface Qualification {
  id: string;
  name: string;
  code: string;
}

/** Extract display name from a qualification entry (handles both string and object forms) */
function getQualName(qual: QualificationEntry | string): string {
  if (typeof qual === 'string') return qual;
  return qual?.qualification?.name ?? 'Unknown';
}

// ─── Role badge colors ──────────────────────────────────────

const roleColors: Record<string, string> = {
  ADMIN: 'bg-red-50 text-red-700',
  MANAGER: 'bg-purple-50 text-purple-700',
  SUPERVISOR: 'bg-blue-50 text-blue-700',
  EMPLOYEE: 'bg-gray-100 text-gray-600',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700',
  INACTIVE: 'bg-gray-100 text-gray-600',
  ON_LEAVE: 'bg-amber-50 text-amber-700',
  SUSPENDED: 'bg-red-50 text-red-700',
};

// ─── Component ──────────────────────────────────────────────

export function UsersPage() {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    employeeId: '',
    role: 'EMPLOYEE' as Role,
    qualifications: [] as string[],
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', roleFilter],
    queryFn: () =>
      apiGet<{ data: User[] }>(API_ROUTES.USERS, {
        params: { role: roleFilter || undefined },
      }),
    select: (res) => res.data,
  });

  // Fetch available qualifications from API
  const { data: qualificationsData } = useQuery({
    queryKey: ['qualifications'],
    queryFn: () =>
      apiGet<{ data: Qualification[] }>(`${API_ROUTES.RESOURCES}/qualifications`),
    select: (res) => res.data,
  });

  const allQualifications = qualificationsData ?? [];

  /** Build the payload — map selected qualification IDs and strip the local `qualifications` field */
  function buildPayload(data: typeof formData) {
    const { qualifications, ...rest } = data;
    return { ...rest, qualificationIds: qualifications };
  }

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => apiPost(API_ROUTES.USERS, { ...buildPayload(data), baseId: authUser?.baseId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setFormError(null);
      closeForm();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to create user');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      apiPut(`${API_ROUTES.USERS}/${id}`, buildPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setFormError(null);
      closeForm();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to update user');
    },
  });

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.USERS}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteError(null);
    },
    onError: (err: any) => {
      setDeleteError(err?.response?.data?.error || err?.message || 'Failed to delete user');
    },
  });

  const userList = users ?? [];

  const filteredUsers = userList.filter((u) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.employeeId.toLowerCase().includes(q)
      );
    }
    return true;
  });

  function openCreate() {
    setEditingUser(null);
    setFormError(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      employeeId: '',
      role: 'EMPLOYEE' as Role,
      qualifications: [],
    });
    setShowForm(true);
  }

  function openEdit(user: User) {
    setEditingUser(user);
    setFormError(null);
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      employeeId: user.employeeId,
      role: user.role,
      qualifications: user.qualifications.map((q) =>
        typeof q === 'string' ? q : (q as any).qualificationId ?? (q as any).qualification?.id ?? ''
      ).filter(Boolean),
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingUser(null);
  }

  function handleSave() {
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  function toggleQualification(qual: string) {
    setFormData((prev) => ({
      ...prev,
      qualifications: prev.qualifications.includes(qual)
        ? prev.qualifications.filter((q) => q !== qual)
        : [...prev.qualifications, qual],
    }));
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts, roles, and qualifications
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </button>
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <div className="card border-aviation-200 bg-aviation-50/30">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingUser ? 'Edit User' : 'New User'}
            </h3>
            <button onClick={closeForm} className="rounded-lg p-1 text-gray-400 hover:bg-gray-200">
              <X className="h-5 w-5" />
            </button>
          </div>

          {formError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                className="input-field"
                placeholder="First name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                className="input-field"
                placeholder="Last name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Employee ID</label>
              <input
                type="text"
                value={formData.employeeId}
                onChange={(e) => setFormData((p) => ({ ...p, employeeId: e.target.value }))}
                className="input-field"
                placeholder="EMP-XXX"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="input-field"
                placeholder="user@company.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value as Role }))}
                className="input-field"
              >
                <option value="EMPLOYEE">Employee</option>
                <option value="SUPERVISOR">Supervisor</option>
                <option value="MANAGER">Manager</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
          </div>

          {/* Qualifications */}
          {allQualifications.length > 0 && (
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Qualifications</label>
              <div className="flex flex-wrap gap-2">
                {allQualifications.map((qual) => (
                  <button
                    key={qual.id}
                    type="button"
                    onClick={() => toggleQualification(qual.id)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors
                      ${
                        formData.qualifications.includes(qual.id)
                          ? 'border-aviation-500 bg-aviation-50 text-aviation-700'
                          : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                  >
                    {formData.qualifications.includes(qual.id) && (
                      <BadgeCheck className="mr-1 inline h-3.5 w-3.5" />
                    )}
                    {qual.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={closeForm} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={
                !formData.email || !formData.firstName || !formData.lastName ||
                createMutation.isPending || updateMutation.isPending
              }
              className="btn-primary"
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {editingUser ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name, email, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="input-field w-auto"
        >
          <option value="">All roles</option>
          <option value="ADMIN">Admin</option>
          <option value="MANAGER">Manager</option>
          <option value="SUPERVISOR">Supervisor</option>
          <option value="EMPLOYEE">Employee</option>
        </select>
      </div>

      {/* Error banner */}
      {deleteError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="ml-2 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* User table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">User</th>
                  <th className="table-header">Employee ID</th>
                  <th className="table-header">Role</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Qualifications</th>
                  <th className="table-header">Last Login</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-aviation-100">
                          <UserCircle className="h-5 w-5 text-aviation-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell font-mono text-gray-500">{user.employeeId}</td>
                    <td className="table-cell">
                      <span className={`badge ${roleColors[user.role] || 'bg-gray-100 text-gray-600'}`}>
                        <Shield className="mr-1 h-3 w-3" />
                        {user.role}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${statusColors[user.status] || 'bg-gray-100 text-gray-600'}`}>
                        {user.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {user.qualifications.slice(0, 3).map((qual, idx) => (
                          <span key={getQualName(qual) + idx} className="badge bg-blue-50 text-blue-700">
                            {getQualName(qual)}
                          </span>
                        ))}
                        {user.qualifications.length > 3 && (
                          <span className="badge bg-gray-100 text-gray-500">
                            +{user.qualifications.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-gray-500">
                      {user.lastLogin
                        ? dayjs(user.lastLogin).format('DD MMM, HH:mm')
                        : 'Never'}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(user)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Edit user"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        {authUser?.role === 'ADMIN' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
                                deleteMutation.mutate(user.id);
                              }
                            }}
                            className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Delete user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-sm text-gray-400">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
